import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import RadioCard from "../../../../../ui/RadioCard/RadioCard";
import Button from "../../../../../ui/Button/Button";
import styles from "./ConfirmKeywords.module.css";
import { zodResolver } from "@hookform/resolvers/zod";

const schema = z.object({
  keywords: z
    .object({ keyword: z.string().min(1, { message: "Required" }) })
    .array()
    .min(1, { message: "Required" }),
  status: z.enum(["draft", "generate"]),
});

type ConfirmKeywordsFormData = z.infer<typeof schema>;

interface ConfirmKeywordsProps {
  onBack: () => void;
  onSubmit: (formData: ConfirmKeywordsFormData) => void;
  defaultValues?: ConfirmKeywordsFormData;
}

function ConfirmKeywords({
  onBack,
  onSubmit,
  defaultValues,
}: ConfirmKeywordsProps) {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<ConfirmKeywordsFormData>({
    defaultValues: defaultValues ?? { status: "generate" },
    resolver: zodResolver(schema),
  });

  const status = useWatch({ control, name: "status" });
  const keywords = useWatch({ control, name: "keywords" });

  return (
    <>
      <div>
        <Controller
          control={control}
          name="status"
          render={({ field: { onChange, name, ref, value } }) => (
            <div className={styles.radioGroup}>
              <RadioCard
                checked={value === "generate"}
                description={`You will start generating articles for ${
                  keywords.length === 1
                    ? "1 new keyword"
                    : `all ${keywords.length} new keywords`
                }.`}
                name={name}
                onChange={() => {
                  onChange("generate");
                }}
                ref={ref}
                title="Generate Articles"
                value="generate"
              />
              <RadioCard
                checked={value === "draft"}
                description="Keywords will be added to your article list as drafts"
                name={name}
                onChange={() => {
                  onChange("draft");
                }}
                ref={ref}
                title="Add as Draft"
                value="draft"
              />
            </div>
          )}
        />
      </div>
      <div className={styles.footerButtons}>
        <Button onClick={onBack} variant="outline">
          Back
        </Button>
        <Button
          disabled={isSubmitting}
          loading={isSubmitting}
          onClick={() => handleSubmit(onSubmit)()}
          variant="primary"
        >
          {status === "generate"
            ? `Add Keywords (${keywords.length} ${
                keywords.length === 1 ? "credit" : "credits"
              })`
            : "Add Keywords (Free)"}
        </Button>
      </div>
    </>
  );
}

export default ConfirmKeywords;
